// SuiteDrawer — suite drill-in (Phase 3, 2026-07-17).
// Slide-in drawer (bottom sheet on mobile) for one member facility: access
// codes, assigned sub + crew, RAG + reason, its billing line, open feedback /
// task counts, deep link to the full Facility Detail page. Lazy-fetches on
// open; every section fails soft.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { supabase } from '../../supabase';
import { overlayClickClose } from '../../overlay-close';
import { fmtBareDate } from '../../format';
import { summariseCrew, type CrewLine } from '../crew-summary';
import { DRAWER_ACCESS_SELECT, DRAWER_CREW_SELECT, PROVIDER_NAME_SELECT } from '../queries';
import type { BuildingMember } from '../types';

/** What the crew row says when the crew could not be read — never a bare dash. */
export const CREW_LOAD_FAILED = "Couldn't load the crew — reopen to try again";

interface DrawerData {
  alarm_code: string | null;
  lock_code: string | null;
  key_location: string | null;
  other_access_notes: string | null;
  /** The whole "who cleans this" sentence, crew and start dates included. */
  crew_text: string | null;
  rag_reason: string | null;
  open_task_count: number;
}

export function SuiteDrawer({
  facilityId,
  member,
  facilityNameFor = (id) => id,
  onClose,
}: {
  facilityId: string | null;
  member: BuildingMember | null;
  /** Names the facility a line rolls up to, instead of its bare id. */
  facilityNameFor?: (facilityId: string) => string;
  onClose: () => void;
}) {
  const [data, setData] = useState<DrawerData | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!facilityId) {
      setData(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [accessRes, saRes, facRes, taskRes] = await Promise.all([
          supabase
            .from('facility_access')
            .select(DRAWER_ACCESS_SELECT)
            .eq('facility_id', facilityId)
            .maybeSingle(),
          supabase
            .from('service_assignments')
            .select(DRAWER_CREW_SELECT)
            .eq('facility_id', facilityId)
            // ⛔ Active AND Scheduled. A crew that starts in a fortnight is
            // recorded, agreed and named, and an em dash here reads as missing
            // data. summariseCrew() says which is which. Ben, 2026-09-17.
            .in('assignment_status', ['Active', 'Scheduled']),
          supabase.from('facilities').select('rag_reason').eq('facility_id', facilityId).maybeSingle(),
          supabase
            .from('facility_tasks')
            .select('task_id', { count: 'exact', head: true })
            .eq('facility_id', facilityId)
            .eq('status', 'Open'),
        ]);

        // ⛔ A failed read is said out loud. Until 2026-10-19 this lookup named a
        // column that does not exist; the refusal was ignored and the crew row
        // read "—" on every suite, which looks exactly like "no crew".
        let crewText: string | null;
        if (saRes.error) {
          console.warn('[suite-drawer] crew lines lookup failed', saRes.error);
          crewText = CREW_LOAD_FAILED;
        } else {
          const crewLines = (saRes.data ?? []) as CrewLine[];
          const providerIds = [
            ...new Set(crewLines.map((r) => r.provider_id).filter((p): p is string => !!p)),
          ];
          const nameById = new Map<string, string>();
          let namesFailed = false;
          if (providerIds.length) {
            const { data: provs, error: provErr } = await supabase
              .from('service_providers')
              .select(PROVIDER_NAME_SELECT)
              .in('provider_id', providerIds);
            if (provErr) {
              console.warn('[suite-drawer] cleaner name lookup failed', provErr);
              namesFailed = true;
            }
            for (const p of (provs ?? []) as { provider_id: string; partner_name: string | null }[]) {
              if (p.partner_name?.trim()) nameById.set(p.provider_id, p.partner_name.trim());
            }
          }
          crewText = namesFailed
            ? CREW_LOAD_FAILED
            : summariseCrew(crewLines, (id) => nameById.get(id)).text;
        }

        if (!cancelled) {
          setData({
            alarm_code: accessRes.data?.alarm_code ?? null,
            lock_code: accessRes.data?.lock_code ?? null,
            key_location: accessRes.data?.key_location ?? null,
            other_access_notes: accessRes.data?.other_access_notes ?? null,
            crew_text: crewText,
            rag_reason: (facRes.data as { rag_reason: string | null } | null)?.rag_reason ?? null,
            open_task_count: taskRes.count ?? 0,
          });
        }
      } catch {
        if (!cancelled) setData(null); // fail soft — drawer still renders basics
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [facilityId]);

  // Escape closes
  useEffect(() => {
    if (!facilityId) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [facilityId, onClose]);

  if (!facilityId || !member) return null;

  const line = member.billing_lines[0] ?? null;

  return (
    <div
      className="fixed inset-0 z-50 bg-tcs-scrim/60"
      {...overlayClickClose(onClose)}
      role="dialog"
      aria-modal="true"
      aria-label={`${member.facility_name} quick view`}
    >
      <div className="absolute inset-x-0 bottom-0 max-h-[85vh] overflow-y-auto rounded-t-2xl bg-tcs-bg p-4 shadow-xl md:inset-y-0 md:left-auto md:right-0 md:w-[420px] md:max-h-none md:rounded-none">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="truncate text-sm font-semibold text-tcs-text">{member.facility_name}</h3>
            <p className="text-[11px] text-tcs-text-secondary">
              {member.suite ? `Suite ${member.suite} · ` : ''}
              {member.account_name ?? member.account_id ?? '—'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-tcs-text-secondary hover:bg-tcs-surface"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <dl className="mt-3 space-y-3 text-sm">
          <DrawerRow label="RAG">
            {member.account_status_rag ?? '—'}
            {data?.rag_reason ? ` — ${data.rag_reason}` : ''}
          </DrawerRow>
          <DrawerRow label="Assigned sub / crew">
            {loading ? 'Loading…' : (data?.crew_text ?? '—')}
          </DrawerRow>
          <DrawerRow label="Access">
            {loading ? 'Loading…' : (
              <>
                {data?.alarm_code ? `Alarm ${data.alarm_code}` : null}
                {data?.lock_code ? `${data?.alarm_code ? ' · ' : ''}Lock ${data.lock_code}` : null}
                {data?.key_location ? `${data?.alarm_code || data?.lock_code ? ' · ' : ''}Keys: ${data.key_location}` : null}
                {!data?.alarm_code && !data?.lock_code && !data?.key_location && '—'}
              </>
            )}
          </DrawerRow>

          {data?.other_access_notes && (
            <DrawerRow label="Access notes">{data.other_access_notes}</DrawerRow>
          )}
          <DrawerRow label="Billing line (read-only)">
            {line ? (
              <>
                {line.billing_rate != null
                  ? Number(line.billing_rate).toLocaleString('en-US', { style: 'currency', currency: 'USD' }) + '/mo'
                  : '—'}
                {line.billing_method ? ` · ${line.billing_method.replace(/_/g, ' ')}` : ''}
                {line.cost_group_facility_id && line.cost_group_facility_id !== member.facility_id
                  ? ` · rolls to ${facilityNameFor(line.cost_group_facility_id)}`
                  : ''}
              </>
            ) : (
              '—'
            )}
          </DrawerRow>
          <DrawerRow label="Open items">
            {member.open_feedback_count} feedback ·{' '}
            {loading ? '…' : (() => {
              const n = data?.open_task_count ?? 0;
              return `${n} ${n === 1 ? 'task' : 'tasks'}`;
            })()}
            {member.last_feedback_date ? ` · last feedback ${fmtBareDate(member.last_feedback_date)}` : ''}
          </DrawerRow>
          {member.total_sqft != null && (
            <DrawerRow label="Square footage">{member.total_sqft.toLocaleString()} SF</DrawerRow>
          )}
        </dl>

        <Link
          to={`/account-management/facilities/${member.facility_id}`}
          className="mt-4 flex min-h-[48px] items-center justify-center rounded-lg bg-tcs-inverse text-sm font-semibold text-tcs-bg hover:opacity-90"
          onClick={onClose}
        >
          Open full facility →
        </Link>
      </div>
    </div>
  );
}

function DrawerRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-[10px] font-semibold uppercase tracking-[0.08em] text-tcs-text-muted">{label}</dt>
      <dd className="mt-0.5 text-sm text-tcs-text">{children}</dd>
    </div>
  );
}
